import React from "react";
import { Paper, Typography, Box, Avatar, Chip, useTheme } from "@mui/material";
import { UserProfile } from "../../types";
import { getStaticAssetPath } from "../../utils/staticAssets";
import {
  IconTitle,
  IndustriesIcon,
  LocationTitle,
  SchoolTitle,
} from "../ui/UserIcons";

interface UserProfileCardProps {
  userData: UserProfile;
  onCategoryClick: (type: string, value: string) => void;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  userData,
  onCategoryClick,
}) => {
  const theme = useTheme();

  const getUserInitials = (user: UserProfile): string => {
    const name = `${user.name} ${user.last_name}`;
    const rgx = /(\p{L}{1})\p{L}+/gu;
    const initials = [...name.matchAll(rgx)];
    return (
      (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")
    ).toUpperCase();
  };

  return (
    <Paper elevation={0} sx={{ overflow: "hidden", mb: 3 }}>
      {/* Background Header */}
      <Box
        sx={{
          height: 200,
          backgroundImage: `url(${getStaticAssetPath("images/userBg.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      />
      {/* Profile Content */}
      <Box sx={{ px: 3, pb: 3, textAlign: "center", mt: -9 }}>
        {/* Profile Image/Avatar */}
        <Avatar
          src={userData.image && getStaticAssetPath(userData.image)}
          sx={{
            width: 144,
            height: 144,
            mx: "auto",
            mb: 2,
            border: "4px solid white",
            boxShadow: 2,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.materiom.eggshell,
            fontSize: "2rem",
            fontWeight: "bold",
          }}
        >
          {!userData.image && getUserInitials(userData)}
        </Avatar>

        {/* User Name */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: theme.palette.materiom.seaweed,
            mb: 1,
          }}
        >
          {userData.name} {userData.last_name}
        </Typography>

        {/* Location and Institute */}
        <LocationTitle location={userData.locationName} />
        <SchoolTitle institute={userData.institute} />

        {/* Industries & Disciplines */}
        <Box sx={{ textAlign: "left", mt: 3 }}>
          <IconTitle
            Icon={IndustriesIcon}
            title="Industries & Disciplines"
            color={theme.palette.materiom.seaweed}
          />

          {(userData.disciplines.length > 0 ||
            userData.industries.length > 0) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {userData.disciplines.map((discipline) => (
                <Chip
                  key={discipline.value}
                  label={discipline.label}
                  size="small"
                  variant="outlined"
                  // TODO: Add navigation functionality for discipline chips
                  // onClick={() =>
                  //   onCategoryClick("discipline", discipline.value)
                  // }
                  sx={{
                    cursor: "default",
                  }}
                />
              ))}
              {userData.industries.map((industry) => (
                <Chip
                  key={industry.value}
                  label={industry.label}
                  size="small"
                  variant="outlined"
                  // TODO: Add navigation functionality for industry chips
                  // onClick={() => onCategoryClick("industry", industry.value)}
                  sx={{
                    cursor: "default",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default UserProfileCard;
